<template>
  <div
    :id="`${id}_wrap`"
    class="datepicker-wrap"
    v-click-outside="onClickOutside">
    <input
      v-if="formInputName"
      type="hidden"
      :name="formInputName"
      :value="internalValue" />
    <label
      v-if="label"
      :id="`${id}_label`"
      class="datepicker-label"
      :for="`${id}_button`"
      >{{ label }}</label
    >
    <button
      ref="button"
      v-if="dropdownStyle"
      :id="`${id}_button`"
      type="button"
      :class="{
        'datepicker-button': true,
        'datepicker-button--state-disabled': disabled,
        'datepicker-button--state-error': error,
        'datepicker-button--state-focus': open,
        'validation-field': true
      }"
      :aria-labelledby="`${id}_label ${id}_button`"
      :disabled="disabled"
      aria-haspopup="dialog"
      :aria-expanded="showDropdown"
      @keydown="onButtonKeyboardNav"
      @click="onClickButton">
      <span class="datepicker-button__text">{{
        convertDateToString(internalValue)
      }}</span>
      <svgicon
        aria-hidden="true"
        original
        class="datepicker-button__icon"
        data="@iconPath/outline/16/calendar.svg?inline"
        width="16"
        height="16"></svgicon>
    </button>
    <div
      v-if="showDropdown"
      ref="dialog"
      :id="`${id}_dialog_wrap`"
      :class="{
        'datepicker': true,
        'datepicker--is-static': !dropdownStyle,
        'datepicker--state-open': showDropdown,
        'datepicker--right': openRight
      }"
      :role="dropdownStyle ? 'dialog' : ''"
      :aria-modal="dropdownStyle ? true : false"
      aria-label="Choose date"
      @keydown="onDropdownKeyboardNav">
      <div
        v-if="showDateSelection"
        class="datepicker-selection"
        @focusout="onDateSelectionFocusout">
        <template
          v-for="fType in formatOrder"
          :key="fType">
          <div
            v-if="fType === 'm'"
            class="datepicker-selection__item datepicker-selection__month">
            <select
              :id="`${id}_month_selection_button`"
              style="display: none"></select>
            <combobox-component
              label="Month"
              :id="`${id}_month_selection`"
              :items="options.months"
              :modelValue="[dates.selected.getMonth()]"
              @change="onSelectionChange('month', $event)"></combobox-component>
          </div>
          <div
            v-if="fType === 'd'"
            class="datepicker-selection__item datepicker-selection__day">
            <select
              :id="`${id}_day_selection_button`"
              style="display: none"></select>
            <combobox-component
              label="Day"
              :id="`${id}_day_selection`"
              :items="options.days"
              :modelValue="[dates.selected.getDate()]"
              @change="onSelectionChange('day', $event)"></combobox-component>
          </div>
          <div
            v-if="fType === 'y'"
            class="datepicker-selection__item datepicker-selection__year">
            <select
              :id="`${id}_year_selection_button`"
              style="display: none"></select>
            <combobox-component
              label="Year"
              :id="`${id}_year_selection`"
              :items="options.years"
              :modelValue="[dates.selected.getFullYear()]"
              @change="onSelectionChange('year', $event)"></combobox-component>
          </div>
        </template>
      </div>

      <div
        v-if="showCalendar"
        class="datepicker-calendar">
        <div class="datepicker-header">
          <button
            type="button"
            class="datepicker-header__button"
            aria-label="Previous Month"
            @click="onClickCalendarChangeMonth(-1)">
            <svgicon
              aria-hidden="true"
              original
              class="datepicker-header__button-icon"
              data="@iconPath/glyph/16/small-triangle-left.svg?inline"
              width="16"
              height="16"></svgicon>
          </button>
          <h2
            :id="`${id}_calendar_title`"
            class="datepicker-header__title"
            aria-live="polite">
            {{ monthLabels[dates.selected.getMonth()] }}
            {{ dates.selected.getFullYear() }}
          </h2>
          <button
            type="button"
            class="datepicker-header__button"
            aria-label="Next Month"
            @click="onClickCalendarChangeMonth(1)">
            <svgicon
              aria-hidden="true"
              original
              class="datepicker-header__button-icon"
              data="@iconPath/glyph/16/small-triangle-right.svg?inline"
              width="16"
              height="16"></svgicon>
          </button>
        </div>

        <table
          role="grid"
          ref="grid"
          class="datepicker-calendar__grid"
          :aria-labelledby="`${id}_calendar_title`"
          @focusin="onCalendarFocusin"
          @focusout="onCalendarFocusout"
          @keydown="onCalendarKeydown">
          <thead>
            <tr>
              <th
                scope="col"
                class="datepicker-calendar__cell datepicker-calendar__weekday"
                :aria-label="weekdayLabels[0]">
                {{ getShortLabel(weekdayLabels[0], 1) }}
              </th>
              <th
                scope="col"
                class="datepicker-calendar__cell datepicker-calendar__weekday"
                :aria-label="weekdayLabels[1]">
                {{ getShortLabel(weekdayLabels[1], 1) }}
              </th>
              <th
                scope="col"
                class="datepicker-calendar__cell datepicker-calendar__weekday"
                :aria-label="weekdayLabels[2]">
                {{ getShortLabel(weekdayLabels[2], 1) }}
              </th>
              <th
                scope="col"
                class="datepicker-calendar__cell datepicker-calendar__weekday"
                :aria-label="weekdayLabels[3]">
                {{ getShortLabel(weekdayLabels[3], 1) }}
              </th>
              <th
                scope="col"
                class="datepicker-calendar__cell datepicker-calendar__weekday"
                :aria-label="weekdayLabels[4]">
                {{ getShortLabel(weekdayLabels[4], 1) }}
              </th>
              <th
                scope="col"
                class="datepicker-calendar__cell datepicker-calendar__weekday"
                :aria-label="weekdayLabels[5]">
                {{ getShortLabel(weekdayLabels[5], 1) }}
              </th>
              <th
                scope="col"
                class="datepicker-calendar__cell datepicker-calendar__weekday"
                :aria-label="weekdayLabels[6]">
                {{ getShortLabel(weekdayLabels[6], 1) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(week, index) in weeks"
              :key="index">
              <td
                v-for="day in week"
                :key="day.key"
                :ref="day.date ? day.date.toISOString() : null"
                :tabindex="day.date && day.selected ? 0 : -1"
                :aria-selected="
                  day.date && convertDateToString(day.date) === modelValue
                    ? true
                    : null
                "
                @click="
                  day.date && !day.disabled
                    ? onClickCalendarDayButton(day.date)
                    : null
                "
                :class="{
                  'datepicker-calendar__cell': true,
                  'datepicker-calendar__button': true,
                  'datepicker-calendar__button--state-today': day.today,
                  'datepicker-calendar__button--state-selected': day.selected,
                  'datepicker-calendar__button--state-disabled':
                    day.disabled || !day.date
                }">
                {{ day.date ? day.date.getDate() : '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="datepicker-buttons">
        <button
          type="button"
          class="datepicker-buttons__button button secondary"
          @click="onClickDialogButton('today')">
          Today
        </button>
        <button
          type="button"
          class="datepicker-buttons__button button reset"
          @click="onClickDialogButton('reset')">
          Reset
        </button>
      </div>

      <button
        type="button"
        class="datepicker-button-primary button primary"
        @click="onClickDialogButton('done')"
        @focusout="onDoneButtonFocusout">
        Done
      </button>
      <div
        :class="{
          'info-note': true,
          'empty': infoText === ''
        }"
        aria-live="polite">
        {{ infoText }}
      </div>
    </div>
  </div>
</template>

<script>
import ComboboxComponent from '@/components/Combobox'
import ClickOutside from '@/directives/clickOutside'

export default {
  name: 'DatePickerComponent',
  components: {
    ComboboxComponent
  },
  directives: {
    ClickOutside
  },
  emits: ['change'],
  props: {
    id: String,
    modelValue: String,
    label: String,
    error: Boolean,
    disabled: Boolean,
    name: String,
    monthLabels: {
      type: Array,
      default() {
        return [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ]
      },
      validator(value) {
        return value.length === 12
      }
    },
    weekdayLabels: {
      type: Array,
      default() {
        return [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday'
        ]
      },
      validator(value) {
        return value.length === 7
      }
    },
    showDateSelection: {
      type: Boolean,
      default: true
    },
    showCalendar: {
      type: Boolean,
      default: true
    },
    dropdownStyle: {
      type: Boolean,
      default: true
    },
    dateFormat: {
      type: String,
      default: 'mm/dd/yyyy'
    },
    dateFormatDelimiter: {
      type: String,
      default: '/'
    },
    minDate: {
      type: String,
      default() {
        return new Date(1900, 0, 1, 0, 0, 0, 0).toISOString()
      }
    },
    maxDate: {
      type: String,
      default() {
        return new Date(2199, 11, 31, 0, 0, 0, 0).toISOString()
      }
    },
    allowedDates: {
      type: Array,
      default() {
        return []
      }
    },
    // This is used if this component is used in a form outside of the legacy code
    formInputName: {
      type: String,
      default: ''
    },
    openRight: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      open: false,
      infoText: '',
      formatOrder: [],
      options: {
        months: [],
        days: [],
        years: []
      },
      internalValue: null,
      dates: {
        min: null,
        current: null,
        selected: null,
        original: null,
        max: null
      },
      weeks: []
    }
  },
  created() {
    this.setInternals()
    this.setInternalDate(this.modelValue)
    this.buildSelectedOptions()
  },
  watch: {
    'modelValue'() {
      if (this.modelValue !== this.convertDateToString(this.internalValue)) {
        this.setInternalDate(this.modelValue)
      }
    },
    'dateFormat'() {
      this.setInternals()
    },
    'dateFormatDelimiter'() {
      this.setInternals()
    },
    'minDate'() {
      this.setInternals()
    },
    'maxDate'() {
      this.setInternals()
    },
    'dates.selected'() {
      if (this.dates.selected.getTime() < this.dates.min.getTime()) {
        this.dates.selected = new Date(this.dates.min.getTime())
      } else if (this.dates.selected.getTime() > this.dates.max.getTime()) {
        this.dates.selected = new Date(this.dates.max.getTime())
      } else if (
        this.isDateDisabled(this.dates.selected) &&
        this.allowedDates.length > 0
      ) {
        this.dates.selected = this.convertStringToDate(this.allowedDates[0])
      }
      this.buildSelectedOptions()
    }
  },
  computed: {
    showDropdown() {
      return this.open
    }
  },
  methods: {
    isDateDisabled(d) {
      if (!(d instanceof Date)) return true

      // Allowed Dates
      if (this.allowedDates.length > 0) {
        let allowed = false

        this.allowedDates.forEach((stringDate) => {
          let d2 = this.convertStringToDate(stringDate)
          if (d2.getTime() === d.getTime()) allowed = true
        })

        return allowed
      }

      // Min/Max Date Ranges
      return (
        d.getTime() < this.dates.min.getTime() ||
        d.getTime() > this.dates.max.getTime()
      )
    },
    convertStringToDate(s) {
      if (s instanceof Date) {
        return s
      }

      if (!s || typeof s !== 'string') {
        return null
      }

      const dateParts = [
        this.dates.current.getFullYear(),
        this.dates.current.getMonth(),
        this.dates.current.getDate()
      ]
      const stringParts = s.split(this.dateFormatDelimiter)

      // Based off the format take the string values out and assign to array for use when building date object
      this.formatOrder.forEach((f, i) => {
        try {
          const value = parseInt(stringParts[i], 10)
          switch (f) {
            case 'm':
              if (value > 0 && value < 13) {
                dateParts[1] = value - 1
              }
              break
            case 'd':
              if (value > 0 && value < 32) {
                dateParts[2] = value
              }
              break
            case 'y':
              if (value > 1000 && value < 9999) {
                dateParts[0] = value
              }
              break
          }
        } catch (e) {
          console.log(e)
        }
      })

      // Return proper date
      return this.normalizeDate(
        new Date(dateParts[0], dateParts[1], dateParts[2])
      )
    },
    convertDateToString(d) {
      if (!d || !(d instanceof Date)) {
        return ''
      }

      let s = []
      this.getFormatParts().forEach((f) => {
        s.push(this.getFormatPart(f, d))
      })
      return s.join(this.dateFormatDelimiter)
    },
    setInternals() {
      this.formatOrder = []
      this.getFormatParts().forEach((f) => {
        const format = this.getFormatPart(f)
        if (!this.formatOrder.includes(format)) {
          this.formatOrder.push(format)
        }
      })
      this.dates.min = this.minDate
        ? this.normalizeDate(new Date(this.minDate))
        : this.normalizeDate(new Date(1900, 0, 1, 0, 0, 0, 0))
      this.dates.max = this.maxDate
        ? this.normalizeDate(new Date(this.maxDate))
        : this.normalizeDate(new Date(2199, 11, 31, 0, 0, 0, 0))
      this.dates.current = this.normalizeDate()
      this.dates.selected = this.normalizeDate()
    },
    setInternalDate(s) {
      this.internalValue = this.convertStringToDate(s)
      if (this.internalValue instanceof Date) {
        this.dates.original = this.normalizeDate(
          new Date(this.internalValue.getTime())
        )
        this.dates.selected = this.normalizeDate(
          new Date(this.internalValue.getTime())
        )
      }
    },
    setSelectedMonth(increment) {
      if (isNaN(increment)) return false

      let selectedDay = this.dates.selected.getDate()
      const newDate = new Date(
        this.dates.selected.setMonth(
          this.dates.selected.getMonth() + increment,
          selectedDay
        )
      )

      // if the new date doesn't exist we need the last day of the month instead
      if (newDate.getDate() !== selectedDay) {
        selectedDay = 0
      }

      this.dates.selected = this.normalizeDate(
        new Date(newDate.getFullYear(), newDate.getMonth(), selectedDay)
      )
    },
    setSelectedYear(increment) {
      if (isNaN(increment)) return false

      let selectedDay = this.dates.selected.getDate()
      const newDate = new Date(
        this.dates.selected.getFullYear() + increment,
        this.dates.selected.getMonth(),
        selectedDay
      )

      // if the new date doesn't exist we need the last day of the month instead
      if (newDate.getDate() !== selectedDay) {
        selectedDay = 0
      }

      this.dates.selected = this.normalizeDate(
        new Date(newDate.getFullYear(), newDate.getMonth(), selectedDay)
      )
    },
    getFormatParts() {
      return this.dateFormat.split(this.dateFormatDelimiter) || []
    },
    getFormatPart(f, d) {
      if (d && !(d instanceof Date)) {
        return ''
      }

      switch (f) {
        // MONTH FORMATS
        case 'm':
          return d ? d.getMonth() + 1 : 'm'
        case 'mm':
          return d ? this.padZero(d.getMonth() + 1) : 'm'
        case 'M':
          return d ? this.getShortLabel(this.monthLabels[d.getMonth()], 3) : 'm'
        case 'MM':
          return d ? this.monthLabels[d.getMonth()] : 'm'

        // DAY FORMATS
        case 'd':
          return d ? d.getDate() : 'd'
        case 'dd':
          return d ? this.padZero(d.getDate()) : 'd'
        case 'D':
          return d ? this.getShortLabel(this.weekdayLabels[d.getDay()], 3) : 'd'
        case 'DD':
          return d ? this.weekdayLabels[d.getDay()] : 'd'

        // YEAR FORMATS
        case 'yy':
          return d ? d.getFullYear().toString().substr(-2) : 'y'
        case 'yyyy':
          return d ? d.getFullYear() : 'y'
      }
      return ''
    },
    getShortLabel(v, l) {
      return v.length >= l ? v.substr(0, l) : v
    },
    buildSelectedOptions() {
      this.options.days = []
      this.options.months = []
      this.options.years = []
      this.weeks = []

      let i = 0
      const firstOfMonth = this.normalizeDate(
        new Date(
          this.dates.selected.getFullYear(),
          this.dates.selected.getMonth(),
          1
        )
      )
      const lastOfMonth = this.normalizeDate(
        new Date(
          this.dates.selected.getFullYear(),
          this.dates.selected.getMonth() + 1,
          0
        )
      )
      const lastDayOfMonth = lastOfMonth.getDate()

      // Build Days
      for (i = 1; i <= lastDayOfMonth; i += 1) {
        this.options.days.push({
          label: i,
          value: i
        })
      }

      // Build Months
      for (i = 0; i < 12; i += 1) {
        this.options.months.push({
          label: this.monthLabels[i],
          value: i
        })
      }

      // Build Years
      for (
        i = this.dates.min.getFullYear();
        i <= this.dates.max.getFullYear();
        i += 1
      ) {
        this.options.years.push({
          label: i,
          value: i
        })
      }

      // Build Weeks/Days
      let d = null
      for (i = 1; i <= 6; i += 1) {
        let wd = 0
        let week = []

        for (wd = 0; wd <= 6; wd += 1) {
          if (i === 1 && firstOfMonth.getDay() === wd && !d) {
            d = this.normalizeDate(new Date(firstOfMonth.getTime()))
          } else if (d) {
            d.setDate(d.getDate() + 1)
            if (d.getMonth() !== firstOfMonth.getMonth()) {
              d = null
            }
          }

          if (i === 6 && !d) {
            continue
          }

          let day = {
            key: `week${i}_weekday${wd}`,
            today: false,
            selected: false,
            disabled: false
          }

          if (d) {
            day.date = this.normalizeDate(new Date(d.getTime()))
            day.today = d.getTime() === this.dates.current.getTime()
            day.selected = d.getTime() === this.dates.selected.getTime()
            day.disabled = this.isDateDisabled(d)
          }

          week.push(day)
        }

        this.weeks.push(week)
      }
      return true
    },
    padZero(v) {
      if (!isNaN(v) && v <= 9) return '0' + v
      return v
    },
    normalizeDate(d) {
      if (!(d instanceof Date)) d = new Date()
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
    },
    toggleDialog(open) {
      this.open = open
      if (this.open) {
        this.focusSelectedDate()
      }
    },
    focusSelectedDate() {
      this.$nextTick(() => {
        try {
          if (
            this.dates.selected &&
            this.$refs[this.dates.selected.toISOString()]
          ) {
            this.$refs[this.dates.selected.toISOString()][0].focus()
          }
        } catch (e) {
          console.log(e)
        }
      })
    },
    onClickButton() {
      this.toggleDialog(!this.open)
      if (this.internalValue instanceof Date) {
        this.dates.selected = this.normalizeDate(
          new Date(this.internalValue.getTime())
        )
      } else {
        this.dates.selected = this.normalizeDate()
      }
      this.focusSelectedDate()
    },
    onClickOutside() {
      this.toggleDialog(false)
    },
    onSelectionChange(prop, v) {
      const val = parseInt(v[0], 10)
      this.dates.selected = this.normalizeDate(
        new Date(
          prop === 'year' ? val : this.dates.selected.getFullYear(),
          prop === 'month' ? val : this.dates.selected.getMonth(),
          prop === 'day' ? val : this.dates.selected.getDate()
        )
      )
    },
    onClickDialogButton(opt) {
      switch (opt) {
        case 'done':
          this.toggleDialog(false)
          this.internalValue = this.normalizeDate(new Date(this.dates.selected))
          this.$emit('change', this.convertDateToString(this.internalValue))
          this.$refs.button.focus()
          return
        case 'today':
          this.dates.selected = this.normalizeDate(new Date(this.dates.current))
          return
        case 'reset':
          this.dates.selected = this.normalizeDate(
            new Date(this.dates.original)
          )
          return
      }
    },
    onClickCalendarDayButton(d) {
      this.toggleDialog(false)
      this.dates.selected = this.normalizeDate(new Date(d))
      this.internalValue = this.normalizeDate(new Date(d))
      this.$emit('change', this.convertDateToString(this.internalValue))
    },
    onClickCalendarChangeMonth(increment) {
      this.setSelectedMonth(increment)
    },
    onButtonKeyboardNav(ev) {
      ev.stopPropagation()

      switch (ev.key) {
        case 'Tab':
        case 'Escape':
        case 'ArrowUp':
          this.toggleDialog(false)
          break
        case 'Enter':
          ev.preventDefault()
          this.toggleDialog(!this.open)
          break
        case 'ArrowDown':
          ev.preventDefault()
          this.toggleDialog(true)
          break
      }
      return true
    },
    onDropdownKeyboardNav(ev) {
      switch (ev.key) {
        case 'Escape':
          ev.preventDefault()
          ev.stopPropagation()
          this.toggleDialog(false)
          this.$nextTick(() => {
            this.$refs.button.focus()
          })
          return
      }
      return true
    },

    onDateSelectionFocusout(ev) {
      if (ev.relatedTarget && !this.$refs.dialog.contains(ev.relatedTarget)) {
        try {
          this.$refs.dialog
            .getElementsByClassName('datepicker-button-primary')[0]
            .focus()
        } catch (e) {
          // ideally this should never happen, but if the field can't be focused it would be better to just close the popup (same with the other focusout method)
          this.toggleDialog(false)
        }
      }
    },

    onDoneButtonFocusout(ev) {
      if (
        ev.relatedTarget &&
        !this.$refs.dialog.contains(ev.relatedTarget) &&
        ev.relatedTarget !== this.$refs.button
      ) {
        ev.preventDefault()
        ev.stopPropagation()
        try {
          this.$refs.dialog
            .querySelector('.datepicker-selection__item button')
            .focus()
        } catch (e) {
          this.toggleDialog(false)
        }
      }
    },

    onCalendarFocusin(ev) {
      if (
        ev.target &&
        ev.target.classList.contains('datepicker-calendar__cell')
      ) {
        // timeout needed for the aria live region to be announced (200 seems good enough so far)
        setTimeout(() => {
          if (this.infoText === '') {
            this.infoText = 'Note: Cursor keys can navigate dates.'
          }
        }, 200)
      }
    },

    onCalendarFocusout(ev) {
      if (
        ev.relatedTarget &&
        !ev.relatedTarget.classList.contains('datepicker-calendar__cell')
      ) {
        this.infoText = ''
      }
    },

    onCalendarKeydown(ev) {
      switch (ev.key) {
        case 'PageUp':
          ev.preventDefault()
          ev.stopPropagation()
          if (ev.shiftKey) {
            // show same month dates, previous year; focus the same date (if it exists) or last day of month
            this.setSelectedYear(-1)
          } else {
            // show previous month dates; focus the same date (if it exists) or last day of month
            this.setSelectedMonth(-1)
          }
          this.focusSelectedDate()
          break
        case 'PageDown':
          ev.preventDefault()
          ev.stopPropagation()
          if (ev.shiftKey) {
            // show same month dates, next year; focus the same date (if it exists) or last day of month
            this.setSelectedYear(1)
          } else {
            // show next month dates; focus the same date (if it exists) or last day of month
            this.setSelectedMonth(1)
          }
          this.focusSelectedDate()
          break
        case 'End': // Last day of current selected week
          ev.preventDefault()
          ev.stopPropagation()
          this.dates.selected = this.normalizeDate(
            new Date(
              this.dates.selected.setDate(
                this.dates.selected.getDate() - this.dates.selected.getDay() + 6
              )
            )
          )
          this.focusSelectedDate()
          break
        case 'Home': // First day of current selected week
          ev.preventDefault()
          ev.stopPropagation()
          this.dates.selected = this.normalizeDate(
            new Date(
              this.dates.selected.setDate(
                this.dates.selected.getDate() - this.dates.selected.getDay()
              )
            )
          )
          this.focusSelectedDate()
          break
        case 'ArrowLeft':
          ev.preventDefault()
          ev.stopPropagation()
          this.dates.selected = this.normalizeDate(
            new Date(
              this.dates.selected.setDate(this.dates.selected.getDate() - 1)
            )
          )
          this.focusSelectedDate()
          break
        case 'ArrowUp':
          ev.preventDefault()
          ev.stopPropagation()
          this.dates.selected = this.normalizeDate(
            new Date(
              this.dates.selected.setDate(this.dates.selected.getDate() - 7)
            )
          )
          this.focusSelectedDate()
          break
        case 'ArrowRight':
          ev.preventDefault()
          ev.stopPropagation()
          this.dates.selected = this.normalizeDate(
            new Date(
              this.dates.selected.setDate(this.dates.selected.getDate() + 1)
            )
          )
          this.focusSelectedDate()
          break
        case 'ArrowDown':
          ev.preventDefault()
          ev.stopPropagation()
          this.dates.selected = this.normalizeDate(
            new Date(
              this.dates.selected.setDate(this.dates.selected.getDate() + 7)
            )
          )
          this.focusSelectedDate()
          break
        case 'Enter': // update field value and close the dialog
          ev.preventDefault()
          this.toggleDialog(false)
          this.internalValue = this.dates.selected
          this.$emit('change', this.convertDateToString(this.internalValue))
          this.$refs.button.focus()
          break
        case ' ': // update field value but keep the dialog open
          ev.preventDefault()
          this.internalValue = this.dates.selected
          this.$emit('change', this.convertDateToString(this.internalValue))
          break
      }
      return true
    }
  }
}
</script>
